import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Loader2, AlertCircle } from 'lucide-react';
import { useFactory } from '../../../context/FactoryContext';
import { useCurrentEmployee } from '../../../hooks/useCurrentEmployee';
import { usePermissions } from '../../../hooks/usePermissions';
import { isLaborRestrictedViewer, isProdSupervisorTeamScoped } from '../../../utils/roleChecks';
import { filterTeamsByRole, collectTeamEmployeeIds } from '../../../utils/teamScope';
import { employeeService } from '../empleados/services/employeeService';
import { teamService } from '../empleados/services/teamService';
import {
  laborScheduleService,
  LABOR_CALENDAR_VIEWS,
} from './services/laborScheduleService';
import {
  mapDailyScheduleToEvents,
  mapFactoryScheduleToEvents,
  extractPatternsFromFactoryDays,
} from './utils/scheduleToFullCalendar';
import {
  resolveLaborFactoryId,
  getEmployeeDisplayName,
} from './utils/laborFactoryUtils';
import { attachHolidayIdsToEvents, toDateInputValue } from './utils/laborCalendarUtils';
import { LaborFullCalendar } from './components/LaborFullCalendar';
import { LaborAbsenceLegend } from './components/LaborAbsenceLegend';
import { LaborResourceScheduler } from './components/LaborResourceScheduler';
import { LaborFactoryCalendarSetup } from './components/LaborFactoryCalendarSetup';
import { LaborPatternSetup } from './components/LaborPatternSetup';
import { LaborEmployeePatternSetup } from './components/LaborEmployeePatternSetup';
import { LaborOverrideSetup } from './components/LaborOverrideSetup';
import { LaborHolidayClickModal } from './components/LaborHolidayClickModal';
import { LaborPatternSkillSetup } from './components/LaborPatternSkillSetup';
import { LaborTeamPatternSetup } from './components/LaborTeamPatternSetup';
import { SkillsCatalogSection } from '../empleados/components/SkillsCatalogSection';

const VIEW_OPTIONS = [
  { id: LABOR_CALENDAR_VIEWS.FACTORY, label: 'Fábrica' },
  { id: LABOR_CALENDAR_VIEWS.PATTERN, label: 'Turno' },
  { id: LABOR_CALENDAR_VIEWS.EMPLOYEE, label: 'Empleado' },
  { id: LABOR_CALENDAR_VIEWS.TEAM, label: 'Equipo' },
];

function teamMemberLabel(member, leaderId) {
  const emp = member?.employee || member?.Employee;
  if (emp) return getEmployeeDisplayName(emp);
  if (member?.employee_id && Number(member.employee_id) === Number(leaderId)) {
    return 'Líder del equipo';
  }
  return member?.employee_id ? `Empleado #${member.employee_id}` : 'Miembro';
}

const LABOR_PRESENTATION = {
  CALENDAR: 'calendar',
  SCHEDULER: 'scheduler',
};

const PRESENTATION_OPTIONS = [
  { id: LABOR_PRESENTATION.CALENDAR, label: 'Calendario' },
  { id: LABOR_PRESENTATION.SCHEDULER, label: 'Scheduler' },
];

/**
 * Tablero de calendario laboral, reutilizado por /calendario-labor (Fábrica/Turno)
 * y /asignaciones (Empleado/Equipo). `allowedViews` acota qué vistas puede
 * seleccionar el usuario en esta instancia del tablero.
 */
export const LaborCalendarBoard = ({
  allowedViews,
  title = 'Calendario laboral',
  description,
}) => {
  const { selectedFactory, factories } = useFactory();
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const { roleKeys, isLoading: permissionsLoading } = usePermissions();
  const isViewer = useMemo(() => isLaborRestrictedViewer(roleKeys), [roleKeys]);
  const isSupervisorScoped = useMemo(() => isProdSupervisorTeamScoped(roleKeys), [roleKeys]);
  const isPersonalCalendarLocked = isViewer || isSupervisorScoped;
  const { employeeId: currentEmployeeId, employeeName: currentEmployeeName, isReady: currentEmployeeReady } =
    useCurrentEmployee();

  const [calendarView, setCalendarView] = useState(allowedViews[0]);
  const [presentation, setPresentation] = useState(LABOR_PRESENTATION.CALENDAR);
  const [year, setYear] = useState(currentYear);
  const [employeeId, setEmployeeId] = useState('');
  const [patternId, setPatternId] = useState('');
  const [teamCalendarId, setTeamCalendarId] = useState('');
  const [clickedHoliday, setClickedHoliday] = useState(null);

  const factoryId = useMemo(
    () => resolveLaborFactoryId(selectedFactory, factories),
    [selectedFactory, factories]
  );

  const factoryBase = selectedFactory || '';

  const availableViewOptions = useMemo(() => {
    const base = VIEW_OPTIONS.filter((o) => allowedViews.includes(o.id));
    if (isSupervisorScoped) {
      return base.filter(
        (o) =>
          o.id === LABOR_CALENDAR_VIEWS.EMPLOYEE || o.id === LABOR_CALENDAR_VIEWS.TEAM
      );
    }
    if (isViewer) {
      return base.filter((o) => o.id === LABOR_CALENDAR_VIEWS.EMPLOYEE);
    }
    return base;
  }, [allowedViews, isSupervisorScoped, isViewer]);

  useEffect(() => {
    if (isPersonalCalendarLocked && currentEmployeeReady && currentEmployeeId) {
      setEmployeeId(String(currentEmployeeId));
      if (isViewer && allowedViews.includes(LABOR_CALENDAR_VIEWS.EMPLOYEE)) {
        setCalendarView(LABOR_CALENDAR_VIEWS.EMPLOYEE);
      }
    }
  }, [isPersonalCalendarLocked, isViewer, currentEmployeeReady, currentEmployeeId, allowedViews]);

  useEffect(() => {
    if (
      isSupervisorScoped &&
      [LABOR_CALENDAR_VIEWS.FACTORY, LABOR_CALENDAR_VIEWS.PATTERN].includes(calendarView) &&
      allowedViews.includes(LABOR_CALENDAR_VIEWS.TEAM)
    ) {
      setCalendarView(LABOR_CALENDAR_VIEWS.TEAM);
    }
  }, [isSupervisorScoped, calendarView, allowedViews]);

  const { data: supervisorTeams = [], isLoading: loadingSupervisorTeams } = useQuery({
    queryKey: ['labor-calendar-supervisor-teams', factoryId, currentEmployeeId],
    queryFn: () => teamService.getTeams(),
    enabled: isSupervisorScoped && Boolean(factoryId) && currentEmployeeReady,
    staleTime: 60_000,
    select: (teams) =>
      filterTeamsByRole(teams, {
        factoryId,
        roleKeys,
        leaderEmployeeId: currentEmployeeId,
      }).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  });

  useEffect(() => {
    if (isSupervisorScoped && supervisorTeams.length > 0) {
      setTeamCalendarId(String(supervisorTeams[0].id));
    } else if (!isSupervisorScoped) {
      setTeamCalendarId('');
    }
  }, [isSupervisorScoped, supervisorTeams, factoryId]);

  const { data: teamMembers = [], isLoading: loadingTeamMembers } = useQuery({
    queryKey: ['labor-calendar-team-members', teamCalendarId],
    queryFn: () => teamService.getTeamMembers(teamCalendarId),
    enabled:
      calendarView === LABOR_CALENDAR_VIEWS.TEAM &&
      Boolean(teamCalendarId) &&
      isSupervisorScoped,
  });

  const selectedSupervisorTeam = useMemo(
    () => supervisorTeams.find((t) => String(t.id) === String(teamCalendarId)) ?? null,
    [supervisorTeams, teamCalendarId]
  );

  const teamEmployeeIds = useMemo(() => {
    if (!isSupervisorScoped || !selectedSupervisorTeam) return [];
    return [...collectTeamEmployeeIds(selectedSupervisorTeam, teamMembers)];
  }, [isSupervisorScoped, selectedSupervisorTeam, teamMembers]);

  const { data: employees = [], isLoading: loadingEmployees } = useQuery({
    queryKey: ['labor-calendar-employees', factoryBase],
    queryFn: () => employeeService.getEmployees(factoryBase, true, null),
    enabled:
      calendarView === LABOR_CALENDAR_VIEWS.EMPLOYEE &&
      !isPersonalCalendarLocked,
  });

  const { data: patternsForSelect = [], isLoading: loadingPatterns } = useQuery({
    queryKey: ['labor-calendar-pattern-list', factoryId, year],
    queryFn: async () => {
      const days = await laborScheduleService.getFactoryYearSchedule(factoryId, year);
      return extractPatternsFromFactoryDays(days);
    },
    enabled:
      !isSupervisorScoped &&
      [
        LABOR_CALENDAR_VIEWS.PATTERN,
        LABOR_CALENDAR_VIEWS.EMPLOYEE,
        LABOR_CALENDAR_VIEWS.TEAM,
      ].includes(calendarView) &&
      Boolean(factoryId),
    staleTime: 120_000,
  });

  const scheduleEnabled = useMemo(() => {
    if (calendarView === LABOR_CALENDAR_VIEWS.EMPLOYEE) return Boolean(employeeId);
    if (calendarView === LABOR_CALENDAR_VIEWS.TEAM && isSupervisorScoped) {
      return Boolean(teamCalendarId) && teamEmployeeIds.length > 0 && !loadingTeamMembers;
    }
    if (calendarView === LABOR_CALENDAR_VIEWS.PATTERN) {
      return Boolean(patternId) || Boolean(factoryId);
    }
    if (calendarView === LABOR_CALENDAR_VIEWS.FACTORY) return Boolean(factoryId);
    return false;
  }, [
    calendarView,
    employeeId,
    patternId,
    factoryId,
    isSupervisorScoped,
    teamCalendarId,
    teamEmployeeIds.length,
    loadingTeamMembers,
  ]);

  const needsFactoryCalendar =
    !isSupervisorScoped &&
    [
      LABOR_CALENDAR_VIEWS.FACTORY,
      LABOR_CALENDAR_VIEWS.PATTERN,
      LABOR_CALENDAR_VIEWS.EMPLOYEE,
      LABOR_CALENDAR_VIEWS.TEAM,
    ].includes(calendarView);

  const { data: calendarsForYear = [] } = useQuery({
    queryKey: ['labor-calendars', factoryId, year],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId, year),
    enabled: needsFactoryCalendar && Boolean(factoryId),
  });

  const { data: calendarsAll = [] } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled:
      needsFactoryCalendar &&
      Boolean(factoryId) &&
      calendarsForYear.length === 0,
  });

  const activeCalendarId = useMemo(() => {
    if (calendarsForYear.length > 0) return calendarsForYear[0].id;
    if (calendarsAll.length > 0) return calendarsAll[0].id;
    return null;
  }, [calendarsForYear, calendarsAll]);

  const { data: patternsByCalendar = [] } = useQuery({
    queryKey: ['labor-patterns-by-calendar', activeCalendarId],
    queryFn: () => laborScheduleService.listPatternsByCalendar(activeCalendarId),
    enabled:
      Boolean(activeCalendarId) &&
      !isSupervisorScoped &&
      [
        LABOR_CALENDAR_VIEWS.PATTERN,
        LABOR_CALENDAR_VIEWS.EMPLOYEE,
        LABOR_CALENDAR_VIEWS.TEAM,
      ].includes(calendarView),
    staleTime: 120_000,
  });

  const selectedPatternCycleStart = useMemo(() => {
    if (!patternId) return null;
    const pat = patternsByCalendar.find((p) => String(p.id) === String(patternId));
    return pat?.cycle_start_date ? toDateInputValue(pat.cycle_start_date) : null;
  }, [patternId, patternsByCalendar]);

  const { data: holidaysList = [] } = useQuery({
    queryKey: ['labor-holidays', activeCalendarId],
    queryFn: () => laborScheduleService.listHolidaysByCalendar(activeCalendarId),
    enabled: Boolean(activeCalendarId) && calendarView === LABOR_CALENDAR_VIEWS.FACTORY,
  });

  const {
    data: scheduleData,
    isLoading: loadingSchedule,
    error: scheduleError,
    refetch,
  } = useQuery({
    queryKey: [
      'labor-calendar-schedule',
      calendarView,
      year,
      factoryId,
      employeeId,
      patternId,
      selectedPatternCycleStart,
      teamCalendarId,
      teamEmployeeIds.join(','),
    ],
    queryFn: async () => {
      if (calendarView === LABOR_CALENDAR_VIEWS.TEAM && isSupervisorScoped) {
        const events = [];
        for (const empId of teamEmployeeIds) {
          const days = await laborScheduleService.getEmployeeYearSchedule(empId, year);
          const member = teamMembers.find(
            (m) => Number(m.employee_id ?? m.employee?.id) === Number(empId)
          );
          const isLeader = Number(selectedSupervisorTeam?.leader_id) === Number(empId);
          const entityLabel = member
            ? teamMemberLabel(member, selectedSupervisorTeam?.leader_id)
            : isLeader
              ? currentEmployeeName || 'Líder del equipo'
              : `Empleado #${empId}`;
          events.push(
            ...mapDailyScheduleToEvents(days, {
              view: 'employee',
              entityId: empId,
              entityLabel,
            })
          );
        }
        return { days: [], events };
      }

      if (calendarView === LABOR_CALENDAR_VIEWS.EMPLOYEE) {
        const days = await laborScheduleService.getEmployeeYearSchedule(
          Number(employeeId),
          year
        );
        const emp = employees.find((e) => String(e.id) === String(employeeId));
        const events = mapDailyScheduleToEvents(days, {
          view: 'employee',
          entityId: employeeId,
          entityLabel: getEmployeeDisplayName(emp),
        });
        return { days, events };
      }

      if (calendarView === LABOR_CALENDAR_VIEWS.PATTERN && patternId) {
        const days = await laborScheduleService.getPatternYearSchedule(
          Number(patternId),
          year,
          selectedPatternCycleStart
        );
        const pat = patternsForSelect.find((p) => String(p.id) === String(patternId));
        const events = mapDailyScheduleToEvents(days, {
          view: 'pattern',
          entityId: patternId,
          entityLabel: pat?.name || `Patrón ${patternId}`,
        });
        return { days, events };
      }

      if (
        (calendarView === LABOR_CALENDAR_VIEWS.PATTERN && !patternId) ||
        calendarView === LABOR_CALENDAR_VIEWS.FACTORY
      ) {
        const days = await laborScheduleService.getFactoryYearSchedule(factoryId, year);
        const filterPatternId = calendarView === LABOR_CALENDAR_VIEWS.PATTERN && patternId
          ? Number(patternId)
          : null;
        const events = mapFactoryScheduleToEvents(days, {
          factoryId,
          patternId: filterPatternId,
        });
        return { days, events };
      }

      return { days: [], events: [] };
    },
    enabled: scheduleEnabled,
  });

  const refreshCalendarData = useCallback(
    async ({ deletedPatternId } = {}) => {
      if (deletedPatternId != null && String(patternId) === String(deletedPatternId)) {
        setPatternId('');
      }
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['labor-calendar-pattern-list'] }),
        queryClient.refetchQueries({ queryKey: ['labor-patterns-by-calendar'] }),
        queryClient.refetchQueries({ queryKey: ['labor-calendar-schedule'] }),
      ]);
    },
    [queryClient, patternId]
  );

  const events = useMemo(() => {
    const base = scheduleData?.events ?? [];
    if (calendarView !== LABOR_CALENDAR_VIEWS.FACTORY) return base;
    return attachHolidayIdsToEvents(base, holidaysList);
  }, [scheduleData?.events, holidaysList, calendarView]);

  const initialDate = `${year}-01-15`;

  const showFactoryWarning = needsFactoryCalendar && !factoryId;
  const showEmployeeWarning =
    calendarView === LABOR_CALENDAR_VIEWS.EMPLOYEE && !employeeId;
  const showSelectFactoryHeader =
    calendarView === LABOR_CALENDAR_VIEWS.FACTORY && !selectedFactory;

  const handleViewChange = (view) => {
    setCalendarView(view);
    if (view !== LABOR_CALENDAR_VIEWS.EMPLOYEE) {
      if (!isPersonalCalendarLocked) setEmployeeId('');
    } else if (isPersonalCalendarLocked && currentEmployeeId) {
      setEmployeeId(String(currentEmployeeId));
    }
    if (view !== LABOR_CALENDAR_VIEWS.PATTERN) setPatternId('');
  };

  const isTeamView = calendarView === LABOR_CALENDAR_VIEWS.TEAM;
  const isSupervisorTeamCalendar = isTeamView && isSupervisorScoped;
  const showTeamSetupPanel = isTeamView && !isSupervisorScoped;
  const canEditEmployeeCalendar = !isViewer && !isSupervisorScoped;

  const canShowCalendar =
    !showEmployeeWarning &&
    !showSelectFactoryHeader &&
    !showFactoryWarning &&
    scheduleEnabled;

  const selectedEmployee = employees.find((e) => String(e.id) === String(employeeId));

  if (permissionsLoading) return null;

  return (
    <div className="p-4 sm:p-6 w-full min-w-0">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <CalendarDays className="w-8 h-8 text-blue-400" />
          {title}
        </h1>
        {description && <p className="text-gray-400">{description}</p>}
      </div>

      <div className="mb-4 flex flex-wrap gap-3 items-end bg-gray-800/80 border border-gray-700 rounded-lg p-4">
        {(!isTeamView || isSupervisorTeamCalendar) && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">Presentación</label>
            <div className="flex rounded-lg overflow-hidden border border-gray-600">
              {PRESENTATION_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setPresentation(opt.id)}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${presentation === opt.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {availableViewOptions.length > 1 && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">Vista</label>
            <div className="flex rounded-lg overflow-hidden border border-gray-600">
              {availableViewOptions.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleViewChange(opt.id)}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${calendarView === opt.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                >
                  {isSupervisorScoped && opt.id === LABOR_CALENDAR_VIEWS.EMPLOYEE
                    ? 'Personal'
                    : opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {(!isTeamView || isSupervisorTeamCalendar) && (
          <div>
            <label htmlFor="labor-year" className="block text-xs text-gray-400 mb-1">
              Año
            </label>
            <select
              id="labor-year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm min-w-[100px]"
            >
              {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        )}

        {calendarView === LABOR_CALENDAR_VIEWS.EMPLOYEE && (
          <div className="flex-1 min-w-[200px] max-w-md">
            <label htmlFor="labor-employee" className="block text-xs text-gray-400 mb-1">
              {isSupervisorScoped ? 'Mi calendario' : 'Empleado'}
            </label>
            {isPersonalCalendarLocked ? (
              <div className="w-full bg-gray-700/50 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm">
                {currentEmployeeName || '—'}
              </div>
            ) : (
              <select
                id="labor-employee"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                disabled={loadingEmployees}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Seleccionar empleado…</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {getEmployeeDisplayName(emp)}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {isSupervisorTeamCalendar && (
          <div className="flex-1 min-w-[200px] max-w-sm">
            <label htmlFor="labor-supervisor-team" className="block text-xs text-gray-400 mb-1">
              Tu equipo
            </label>
            <select
              id="labor-supervisor-team"
              value={teamCalendarId}
              onChange={(e) => setTeamCalendarId(e.target.value)}
              disabled={loadingSupervisorTeams || supervisorTeams.length <= 1}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-70"
            >
              {supervisorTeams.length === 0 ? (
                <option value="">Sin equipo asignado</option>
              ) : (
                supervisorTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        {calendarView === LABOR_CALENDAR_VIEWS.PATTERN && (
          <div className="flex-1 min-w-[200px] max-w-md">
            <label htmlFor="labor-pattern" className="block text-xs text-gray-400 mb-1">
              Patrón (vacío = todos)
            </label>
            <select
              id="labor-pattern"
              value={patternId}
              onChange={(e) => setPatternId(e.target.value)}
              disabled={!factoryId || loadingPatterns}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Todos los patrones</option>
              {patternsForSelect.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {calendarView === LABOR_CALENDAR_VIEWS.FACTORY && selectedFactory && (
          <div className="text-sm text-gray-300 pb-2">
            Fábrica: <span className="font-medium text-white">{selectedFactory}</span>
          </div>
        )}

        <button
          type="button"
          onClick={() => refetch()}
          disabled={loadingSchedule || !scheduleEnabled}
          className="ml-auto px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg border border-gray-600 disabled:opacity-50"
        >
          Actualizar
        </button>
      </div>

      {showSelectFactoryHeader && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>Selecciona una fábrica en el header para ver el calendario de planta.</span>
        </div>
      )}

      {showFactoryWarning && !showSelectFactoryHeader && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>
            No se encontró el ID de fábrica para &quot;{selectedFactory}&quot;. Revisa la carga de
            fábricas en el sistema.
          </span>
        </div>
      )}

      {showEmployeeWarning && !isPersonalCalendarLocked && (
        <div className="mb-4 text-gray-400 text-center py-12 border border-dashed border-gray-600 rounded-lg">
          Selecciona un empleado para mostrar su calendario anual.
        </div>
      )}

      {isSupervisorTeamCalendar && !loadingSupervisorTeams && supervisorTeams.length === 0 && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>No tienes ningún equipo asignado como líder.</span>
        </div>
      )}

      {isSupervisorTeamCalendar &&
        teamCalendarId &&
        !loadingTeamMembers &&
        teamEmployeeIds.length === 0 && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>El equipo no tiene miembros asignados para mostrar calendarios.</span>
        </div>
      )}

      {calendarView === LABOR_CALENDAR_VIEWS.FACTORY && factoryId && !showSelectFactoryHeader && (
        <LaborFactoryCalendarSetup
          factoryId={factoryId}
          factoryCode={selectedFactory}
          year={year}
          calendarId={activeCalendarId}
          onAfterChange={() => refetch()}
        />
      )}

      {calendarView === LABOR_CALENDAR_VIEWS.PATTERN && factoryId && !showFactoryWarning && (
        <LaborPatternSetup
          factoryId={factoryId}
          factoryCode={selectedFactory || String(factoryId)}
          year={year}
          calendarId={activeCalendarId}
          onAfterChange={refreshCalendarData}
          onPatternCreated={(id) => setPatternId(id)}
        />
      )}

      {calendarView === LABOR_CALENDAR_VIEWS.PATTERN && patternId && activeCalendarId && (
        <LaborPatternSkillSetup
          patternId={patternId}
          patternName={patternsForSelect.find((p) => String(p.id) === String(patternId))?.name}
          calendarId={activeCalendarId}
          year={year}
          onAfterChange={() => refetch()}
        />
      )}

      {calendarView === LABOR_CALENDAR_VIEWS.EMPLOYEE && employeeId && canEditEmployeeCalendar && (
        <>
          <LaborEmployeePatternSetup
            employeeId={employeeId}
            employeeName={getEmployeeDisplayName(selectedEmployee)}
            factoryId={factoryId}
            year={year}
            calendarId={activeCalendarId}
            patterns={patternsForSelect}
            loadingPatterns={loadingPatterns}
            onAfterChange={() => refetch()}
          />
          <LaborOverrideSetup
            factoryId={factoryId}
            factoryCode={selectedFactory || String(factoryId)}
            year={year}
            calendarId={activeCalendarId}
            defaultEmployeeId={employeeId}
            lockEmployeeScope
            onAfterChange={() => refetch()}
          />
        </>
      )}

      {showTeamSetupPanel && factoryId && (
        <LaborTeamPatternSetup
          factoryId={factoryId}
          calendarId={activeCalendarId}
          patterns={patternsForSelect}
          loadingPatterns={loadingPatterns}
          onAfterChange={() => refetch()}
        />
      )}

      {showTeamSetupPanel && !factoryId && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>Selecciona una fábrica en el header para gestionar equipos.</span>
        </div>
      )}

      {scheduleError && (
        <div className="mb-4 flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-800 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>
            {scheduleError.message ||
              'Error al cargar el calendario. Comprueba que existan calendario, patrones y asignaciones en el backend.'}
          </span>
        </div>
      )}

      {canShowCalendar && (
        <>
          {calendarView === LABOR_CALENDAR_VIEWS.PATTERN && !patternId && (
            <p className="text-gray-500 text-sm mb-2">
              Mostrando todos los patrones de la fábrica. Usa «Crear patrón» arriba o elige uno en el
              desplegable para ver su horario anual.
            </p>
          )}
          {calendarView === LABOR_CALENDAR_VIEWS.PATTERN && patternId && (
            <p className="text-gray-500 text-sm mb-2">
              Calendario anual del patrón seleccionado (GET summary/patterns/year-schedule).
            </p>
          )}
          {presentation === LABOR_PRESENTATION.CALENDAR &&
            calendarView === LABOR_CALENDAR_VIEWS.FACTORY && (
              <p className="text-gray-500 text-sm mb-2">
                Haz clic en un festivo (día gris con nombre) para ver detalle o eliminarlo.
              </p>
            )}
          {presentation === LABOR_PRESENTATION.SCHEDULER && (
            <p className="text-gray-500 text-sm mb-2">
              Scheduler: filas = patrón o empleado; tiempo horizontal. Usa «Semana» para comparar
              varios turnos a la vez y «Día (horas)» para leer franjas horarias con detalle.
            </p>
          )}
          {presentation === LABOR_PRESENTATION.CALENDAR ? (
            <>
              {(calendarView === LABOR_CALENDAR_VIEWS.EMPLOYEE ||
                isSupervisorTeamCalendar) &&
                (employeeId || teamEmployeeIds.length > 0) && <LaborAbsenceLegend />}
              <LaborFullCalendar
                events={events}
                initialDate={initialDate}
                loading={loadingSchedule}
                holidays={calendarView === LABOR_CALENDAR_VIEWS.FACTORY ? holidaysList : []}
                onHolidayClick={
                  calendarView === LABOR_CALENDAR_VIEWS.FACTORY ? setClickedHoliday : undefined
                }
              />
            </>
          ) : (
            <LaborResourceScheduler
              events={events}
              anchorDate={initialDate}
              loading={loadingSchedule}
              onHolidayClick={
                calendarView === LABOR_CALENDAR_VIEWS.FACTORY ? setClickedHoliday : undefined
              }
            />
          )}
          <LaborHolidayClickModal
            holiday={clickedHoliday}
            calendarId={activeCalendarId}
            onClose={() => setClickedHoliday(null)}
            onDeleted={() => refetch()}
          />
          {!loadingSchedule && events.length === 0 && (
            <p className="text-center text-gray-500 mt-4 text-sm">
              Sin eventos para este año. Configura calendario, patrones y asignaciones en la API.
            </p>
          )}
          {calendarView === LABOR_CALENDAR_VIEWS.PATTERN && (
            <SkillsCatalogSection />
          )}
        </>
      )}

      {loadingSchedule && scheduleEnabled && canShowCalendar && (
        <div className="flex justify-center py-4 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Cargando…
        </div>
      )}
    </div>
  );
};

export default LaborCalendarBoard;
